/**
 * ⭐⭐ **O TÓPICO RESTRIÇÕES E PASSIVO NO DESENHO DE IMÓVEIS — 19/09/2026.**
 *
 * O quinto da fila do pedido dele (PRD §46): quadros no alto, uma parte por vez,
 * os botões, a faixa do que foi pesquisado para a parte, uma linha por
 * ocorrência que abre o detalhe.
 *
 * ⛔ **Muda aparência e lugar, nunca o que o botão faz.** O levantamento pago
 * (`levantarEmFila`: um por vez, com o custo à vista, só o master), o CENPROT
 * (`SubirCenprot`), o lançamento à mão (`FormularioDePassivo`) e o apagar que
 * pergunta são as mesmas peças de antes.
 *
 * ⚠️ **Só as partes que o CLIENTE informou**, como sempre (decisão de
 * 21/08/2026), e a tela diz quantas ficaram de fora.
 *
 * ⚠️ **Mudou o LUGAR da marcação:** "marque quem" era uma caixa ao lado de cada
 * parte; com uma parte por vez, a escolha de quem levantar mora num painel
 * próprio, com as caixas e o custo — a mesma fila de antes.
 *
 * ⛔ **Os quadros contam, e não somam dinheiro do caso.** A mesma execução contra
 * codevedores conta UMA vez no total (`passivoDoCaso`, no servidor); somar aqui
 * as partes contaria duas. O total do caso está no índice de cobertura.
 */
import { useEffect, useState } from 'react';
import {
  AJUDA_DA_CATEGORIA,
  apiInvestigados,
  apiTrilha,
  CATEGORIAS_DE_PASSIVO,
  QUALIFICACOES,
  type CategoriaDePassivo,
  type Investigado,
  type LinhaDaTrilha,
  type PassivoDoInvestigado,
} from '../lib/api';
import { usarEhMaster } from '../lib/papel';
import { avisar, avisarErro } from './Avisos';
import { Confirmar } from './Confirmar';
import { FormularioDePassivo, levantarEmFila, SubirCenprot } from './Endividamento';
import { iniciais, Traco } from './ImoveisDoCaso';
import { dizer } from './TrilhaDaApuracao';
import { Valor } from './Valor';
import '../estilos/imoveis-2026.css';

const CATEGORIAS = Object.keys(CATEGORIAS_DE_PASSIVO) as CategoriaDePassivo[];
const emData = (d: string | null | undefined) => (d ? d.split('-').reverse().join('/') : '');
const minuscula = (t: string) => t.charAt(0).toLocaleLowerCase('pt-BR') + t.slice(1);
const PESQUISADA = new Set(['feito', 'nada_consta']);
/* No quadro, o nome cabe numa linha — "Débitos trabalhistas" quebrava e desalinhava os números. */
const NO_QUADRO: Record<CategoriaDePassivo, string> = {
  protesto: 'Protestos',
  divida_ativa: 'Dívida ativa',
  acao_judicial: 'Ações judiciais',
  trabalhista: 'Trabalhistas',
};
const CURTO: Record<CategoriaDePassivo, string> = {
  protesto: 'Protesto',
  divida_ativa: 'Dívida ativa',
  acao_judicial: 'Ação judicial',
  trabalhista: 'Trabalhista',
};

type Painel = null | 'lancar' | 'levantar' | 'cenprot';

export function PassivoDoCaso({ casoId, chave }: { casoId: string; chave: string }) {
  const ehMaster = usarEhMaster();
  const [lista, setLista] = useState<Investigado[] | null>(null);
  const [porParte, setPorParte] = useState<Record<string, PassivoDoInvestigado[]>>({});
  const [trilha, setTrilha] = useState<Record<string, LinhaDaTrilha[]>>({});
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [revisao, setRevisao] = useState(0);
  const [painel, setPainel] = useState<Painel>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [buscando, setBuscando] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void apiInvestigados
      .listar(casoId)
      .then((i) => {
        if (!vivo) return;
        setLista(i.investigados);
        const doCliente = i.investigados.filter((x) => x.origemNoCaso === 'cliente');
        setEscolhida((atual) => atual ?? doCliente[0]?.id ?? null);
      })
      .catch(() => vivo && setLista([]));
    return () => {
      vivo = false;
    };
  }, [casoId, chave]);

  const doCliente = (lista ?? []).filter((i) => i.origemNoCaso === 'cliente');
  const ids = doCliente.map((i) => i.id).join(',');

  useEffect(() => {
    if (!lista) return;
    let vivo = true;
    void Promise.all(
      doCliente.map((p) =>
        apiInvestigados
          .passivos(p.id)
          .then((r) => [p.id, r.passivos] as const)
          .catch(() => [p.id, [] as PassivoDoInvestigado[]] as const),
      ),
    ).then((pares) => {
      if (vivo) setPorParte(Object.fromEntries(pares));
    });
    /* A faixa lê o estado que a trilha já calcula — só leitura, nada consulta. */
    void apiTrilha
      .doCaso(casoId)
      .then((t) => vivo && setTrilha(Object.fromEntries(t.partes.map((p) => [p.investigadoId, p.linhas]))))
      .catch(() => vivo && setTrilha({}));
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, revisao, casoId]);

  if (lista === null) return <p className="ajuda">Carregando…</p>;
  if (doCliente.length === 0) {
    return (
      <p className="ajuda ajuda-solta">
        Nenhum investigado informado pelo cliente neste caso. Esta seção não alcança quem foi trazido para o caso
        durante a investigação.
      </p>
    );
  }

  const promovidos = lista.length - doCliente.length;
  const parte = doCliente.find((p) => p.id === escolhida) ?? doCliente[0]!;
  const passivos = porParte[parte.id];
  const todos = Object.values(porParte).flat();
  const aoMudar = () => setRevisao((n) => n + 1);
  const linhaDe = (id: string) => (trilha[id] ?? []).find((l) => l.chave === 'passivo');
  const levantamento = linhaDe(parte.id);
  const selecionados = doCliente.filter((i) => marcados.has(i.id));
  const custoMarcado = selecionados.reduce((s, i) => s + (i.custoDoPassivo ?? 0), 0);

  async function levantar() {
    await levantarEmFila(
      selecionados.map((i) => i.id),
      setBuscando,
    );
    setMarcados(new Set());
    setPainel(null);
    aoMudar();
  }

  return (
    <div className="imv">
      <Resumo passivos={todos} />

      <div className="imv-pessoas" role="tablist" aria-label="Parte investigada">
        {doCliente.map((p) => {
          const n = porParte[p.id]?.length ?? 0;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={p.id === parte.id}
              className={p.id === parte.id ? 'ativo' : undefined}
              onClick={() => {
                /* O formulário aberto guarda o que foi digitado, e é de uma parte só. */
                if (painel === 'lancar' && p.id !== parte.id) {
                  avisar('Lance ou feche o formulário aberto antes de trocar de parte.');
                  return;
                }
                setEscolhida(p.id);
                if (painel === 'cenprot') setPainel(null);
              }}
            >
              <span className="imv-av">{iniciais(p.nome)}</span>
              <span className="imv-txt">
                <b>{p.nome}</b>
                <small>{QUALIFICACOES[p.qualificacao]}</small>
              </span>
              <span className={`imv-conta${n === 0 ? ' zero' : ''}`} title={n === 0 ? 'nada lançado' : undefined}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      <div className="imv-acoes">
        <button type="button" className="imv-bt primario" aria-expanded={painel === 'lancar'} onClick={() => setPainel(painel === 'lancar' ? null : 'lancar')}>
          <Traco d={['M12 5v14', 'M5 12h14']} />
          Lançar passivo
        </button>
        {/* ⚠️ Só master: o levantamento é pago, e o colaborador não vê valor. */}
        {ehMaster && (
          <button
            type="button"
            className={`imv-bt${painel === 'levantar' ? ' aberto' : ''}`}
            aria-expanded={painel === 'levantar'}
            onClick={() => setPainel(painel === 'levantar' ? null : 'levantar')}
          >
            <Traco d={['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.35-4.35']} />
            Levantar passivo
          </button>
        )}
      </div>

      <div className="imv-fontes">
        <span className="imv-rot">Pesquisado para esta parte</span>
        <span className={`imv-fonte${PESQUISADA.has(levantamento?.palavra ?? '') ? ' ok' : ' falta'}`}>
          <i />
          Levantamento · {levantamento ? minuscula(dizer(levantamento)) : 'em branco'}
        </span>
        {/* ⚠️ O domicílio fica à vista porque é ele que escolhe o órgão das consultas regionais. */}
        <span className={`imv-fonte${parte.domicilio?.uf ? ' ok' : ' falta'}`}>
          <i />
          {parte.domicilio?.uf ? `Domicílio · ${parte.domicilio.municipio}/${parte.domicilio.uf}` : 'Domicílio · sem endereço'}
        </span>
        <span className="imv-esp" />
        {/* ⚠️ O protesto tem caminho próprio: a pessoa pesquisa, a máquina lê. Só master, como antes. */}
        {ehMaster && (
          <button
            type="button"
            className={`imv-bt fantasma p${painel === 'cenprot' ? ' aberto' : ''}`}
            aria-expanded={painel === 'cenprot'}
            onClick={() => setPainel(painel === 'cenprot' ? null : 'cenprot')}
          >
            <Traco d={['M12 16V4', 'm7 9 5-5 5 5', 'M5 20h14']} />
            Pesquisa do CENPROT
          </button>
        )}
      </div>

      {painel === 'lancar' && (
        <div className="imv-painel">
          <FormularioDePassivo
            key={parte.id}
            investigadoId={parte.id}
            titulo={`Lançar passivo · ${parte.nome}`}
            aoMudar={async () => {
              aoMudar();
            }}
          />
        </div>
      )}
      {painel === 'cenprot' && ehMaster && (
        <div className="imv-painel">
          <p className="imv-painel-titulo">Protestos · {parte.nome}</p>
          <SubirCenprot key={parte.id} investigadoId={parte.id} aoMudar={async () => aoMudar()} />
        </div>
      )}
      {painel === 'levantar' && ehMaster && (
        <div className="imv-painel">
          <p className="imv-painel-titulo">Levantar passivo — marque quem</p>
          <p className="ajuda">
            Débitos trabalhistas, ações trabalhistas, dívida ativa federal e estadual, e processos no TJSP.{' '}
            <strong>Protesto não entra:</strong> a fonte foi descontinuada e a consulta segue manual (Pesquisa do
            CENPROT, na faixa acima). As partes são levantadas uma por vez.
          </p>
          <ul className="imv-registros">
            {doCliente.map((i) => (
              <li key={i.id}>
                <label className="imv-registro-nome imv-marcar">
                  <input
                    type="checkbox"
                    checked={marcados.has(i.id)}
                    disabled={buscando !== null}
                    onChange={(e) =>
                      setMarcados((antes) => {
                        const novo = new Set(antes);
                        if (e.target.checked) novo.add(i.id);
                        else novo.delete(i.id);
                        return novo;
                      })
                    }
                  />{' '}
                  {i.nome}
                </label>
                <span className={`imv-registro-situacao${buscando === i.id ? ' feito' : ''}`}>
                  {buscando === i.id
                    ? 'levantando…'
                    : i.domicilio?.uf
                      ? `${i.domicilio.municipio}/${i.domicilio.uf}`
                      : 'sem endereço'}
                </span>
              </li>
            ))}
          </ul>
          <div className="imv-acoes">
            <button
              type="button"
              className="imv-bt primario"
              disabled={selecionados.length === 0 || buscando !== null}
              onClick={() => void levantar()}
              title={
                selecionados.length === 0
                  ? 'Marque as partes cujo passivo quer levantar.'
                  : 'CNDT · CEAT · CND Federal · Lista de Devedores · dívida ativa estadual · processos'
              }
            >
              {buscando
                ? 'Levantando…'
                : selecionados.length === 0
                  ? 'Marque quem levantar'
                  : `Levantar ${selecionados.length} · R$ ${custoMarcado.toFixed(2).replace('.', ',')}`}
            </button>
          </div>
        </div>
      )}

      <ListaDoPassivo passivos={passivos} investigadoId={parte.id} ehMaster={ehMaster} aoMudar={aoMudar} />

      <div className="imv-legenda">
        <span>
          <b>—</b> valor em branco não é zero: não há valor documentado
        </span>
        <span className="imv-legenda-longa">
          <span className="imv-info">i</span> O passivo <strong>não se subtrai</strong> do ativo: a preferência entre
          credores é matéria jurídica, não conta
        </span>
        {promovidos > 0 && (
          <span>
            ⚠️ {promovidos} {promovidos === 1 ? 'parte trazida' : 'partes trazidas'} para o caso durante a investigação{' '}
            {promovidos === 1 ? 'não aparece' : 'não aparecem'} aqui — só as que o cliente informou
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Os quadros: contam por categoria, no caso todo ──────────────────────── */
function Resumo({ passivos }: { passivos: PassivoDoInvestigado[] }) {
  /* O tom do onerado nos quatro: tudo aqui disputa o patrimônio localizado. */
  return (
    <div className="imv-resumo">
      {CATEGORIAS.map((c) => {
        const da = passivos.filter((p) => p.categoria === c);
        const sem = da.filter((p) => p.valor === null).length;
        return (
          <div key={c} className="imv-kpi k-onus">
            <span className="imv-rot" title={CATEGORIAS_DE_PASSIVO[c]}>{NO_QUADRO[c]}</span>
            <strong>{da.length}</strong>
            <small>{da.length === 0 ? 'nada lançado' : sem > 0 ? `${sem} sem valor documentado` : 'todos com valor'}</small>
          </div>
        );
      })}
    </div>
  );
}

/* ── A lista da parte ─────────────────────────────────────────────────────── */
function ListaDoPassivo({
  passivos,
  investigadoId,
  ehMaster,
  aoMudar,
}: {
  passivos: PassivoDoInvestigado[] | undefined;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
}) {
  const [aberto, setAberto] = useState<string | null>(null);
  if (!passivos) return <p className="ajuda">Carregando…</p>;
  const ordem = [...passivos].sort(
    (x, y) => CATEGORIAS.indexOf(x.categoria) - CATEGORIAS.indexOf(y.categoria) || (x.ocorridoEm ?? '9999').localeCompare(y.ocorridoEm ?? '9999'),
  );
  const comValor = passivos.filter((p) => p.valor !== null);
  const total = comValor.reduce((s, p) => s + (p.valor ?? 0), 0);
  const vazias = CATEGORIAS.filter((c) => !passivos.some((p) => p.categoria === c));
  return (
    <>
      <div className="imv-lista">
        <div className="imv-grade imv-cab">
          <span className="imv-rot">Categoria</span>
          <span className="imv-rot">Ocorrência</span>
          <span className="imv-rot">Data do fato</span>
          <span className="imv-rot dir">Valor</span>
          <span />
        </div>
        {ordem.length === 0 ? (
          /* ⚠️ Vazio diz "nada lançado" e NÃO some: ausência de dívida não é ausência de pesquisa. */
          <p className="imv-vazio">Nada lançado para esta parte.</p>
        ) : (
          ordem.map((p) => (
            <LinhaDoPassivo
              key={p.id}
              passivo={p}
              aberto={aberto === p.id}
              aoAbrir={() => setAberto(aberto === p.id ? null : p.id)}
              investigadoId={investigadoId}
              ehMaster={ehMaster}
              aoMudar={aoMudar}
            />
          ))
        )}
      </div>
      {(ordem.length > 0 || vazias.length > 0) && (
        <div className="imv-legenda">
          {ordem.length > 0 && (
            <span>
              Desta parte: <strong><Valor de={total} /></strong> em {passivos.length}{' '}
              {passivos.length === 1 ? 'ocorrência' : 'ocorrências'}
              {passivos.length - comValor.length > 0 && `, ${passivos.length - comValor.length} sem valor documentado`}
            </span>
          )}
          {/* ⚠️ Categoria vazia diz "nada lançado", e não some — é a diferença entre não ter e não ter olhado. */}
          {vazias.length > 0 && <span>Nada lançado em: {vazias.map((c) => CATEGORIAS_DE_PASSIVO[c].toLocaleLowerCase('pt-BR')).join(', ')}</span>}
        </div>
      )}
    </>
  );
}

function LinhaDoPassivo({
  passivo: p,
  aberto,
  aoAbrir,
  investigadoId,
  ehMaster,
  aoMudar,
}: {
  passivo: PassivoDoInvestigado;
  aberto: boolean;
  aoAbrir: () => void;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
}) {
  /* ⚠️ Apagar pergunta antes — pedido dele, 21/08/2026: pode ter custado consulta paga. */
  const [aApagar, setAApagar] = useState(false);
  /* ⭐ 07/10/2026: o processo marcado como irrelevante traz a marca antes da origem de antes. */
  const irrelevante = !!p.origem?.startsWith('irrelevante+');
  const origem = irrelevante ? p.origem!.slice('irrelevante+'.length) || null : p.origem;

  async function apagar() {
    try {
      await apiInvestigados.apagarPassivo(investigadoId, p.id);
      setAApagar(false);
      aoMudar();
    } catch (err) {
      avisarErro(err);
    }
  }

  return (
    <article className={`imv-linha${aberto ? ' aberta' : ''}`}>
      <button type="button" className="imv-grade imv-linha-topo" onClick={aoAbrir} aria-expanded={aberto}>
        <span className="imv-sit-coluna">
          <span className="imv-sit onerado">
            <i />
            {CURTO[p.categoria]}
          </span>
          {p.origem && (
            <span className="imv-tag cinza">
              {irrelevante
                ? 'processo irrelevante'
                : origem === 'djen-provavel'
                  ? 'diário · provável'
                  : origem === 'djen'
                    ? 'diário · confirmado'
                    : 'de consulta'}
            </span>
          )}
        </span>
        <span className="imv-bem">
          <b>{p.descricao}</b>
          {p.referencia && <span className="imv-mono">{p.referencia}</span>}
          {p.situacao && <small>{p.situacao}</small>}
        </span>
        <span className="imv-ato">
          <b>{emData(p.ocorridoEm) || '—'}</b>
        </span>
        <span className="imv-valor">
          {p.valor !== null ? (
            <b>
              <Valor de={p.valor} />
            </b>
          ) : (
            <>
              <b className="na">—</b>
              <small>sem valor documentado</small>
            </>
          )}
        </span>
        <span className="imv-chev" aria-hidden="true">
          <Traco d={[aberto ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6']} />
        </span>
      </button>

      {aberto && (
        <div className="imv-detalhe">
          <div>
            <h5 className="imv-rot">A ocorrência</h5>
            <ul className="imv-mini">
              <li>
                <span>
                  {CATEGORIAS_DE_PASSIVO[p.categoria]} — {AJUDA_DA_CATEGORIA[p.categoria]}
                </span>
              </li>
              <li>
                <span>{p.ocorridoEm ? `Data do fato: ${emData(p.ocorridoEm)}` : 'Data do fato não apurada'}</span>
              </li>
              {p.referencia && (
                <li>
                  <span>Referência: {p.referencia}</span>
                </li>
              )}
              {p.situacao && (
                <li>
                  <span>Situação: {p.situacao}</span>
                </li>
              )}
            </ul>
          </div>
          <div>
            <h5 className="imv-rot">O lançamento</h5>
            <ul className="imv-mini">
              {/* ⚠️ A origem aparece AQUI e em lugar nenhum do documento. */}
              <li>
                <span>
                  {irrelevante
                    ? 'Veio de Processos judiciais relevantes, marcado como irrelevante. A busca de processos não o leva de volta.'
                    : origem?.startsWith('djen')
                      ? `Veio da busca no diário, pelo nome — ${origem === 'djen-provavel' ? 'grau provável: confira a identidade antes de usar' : 'o documento da parte aparece na publicação'}`
                      : origem
                        ? `Veio de consulta${p.coletadoEm ? ` em ${emData(p.coletadoEm.slice(0, 10))}` : ''}`
                        : 'Lançado à mão'}
                </span>
              </li>
              <li>
                <span>
                  {p.valor !== null ? (
                    <>
                      Valor: <Valor de={p.valor} />
                    </>
                  ) : (
                    'Sem valor documentado — não é zero, e não entra na soma'
                  )}
                </span>
              </li>
            </ul>
          </div>
          {ehMaster && (
            <div className="imv-detalhe-acoes">
              <button type="button" className="imv-bt p fantasma perigo" onClick={() => setAApagar(true)}>
                <Traco d={['M4 7h16', 'M9 7V4.5h6V7', 'M6.5 7l1 13h9l1-13']} />
                Apagar
              </button>
            </div>
          )}
        </div>
      )}

      {aApagar && (
        <Confirmar
          titulo="Apagar este lançamento?"
          descricao={`"${p.descricao}"${
            origem
              ? ' — este lançamento veio de consulta, e apagá-lo não devolve o que a consulta custou.'
              : ' — lançado à mão.'
          } O passivo apurado da parte diminui.`}
          aoConfirmar={() => void apagar()}
          aoFechar={() => setAApagar(false)}
        />
      )}
    </article>
  );
}
